import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { memo, useMemo, useEffect, useRef, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import CrmCardLocation from './CrmCardLocation';
import CrmLocationErrorBoundary from './internal/CrmLocationErrorBoundary';
import CrmLocationErrorMessage from './internal/CrmLocationErrorMessage';
import { useCrmLocationService } from './internal/services/CrmLocationService';
import http from 'hub-http/clients/apiClient';
import { LocationStateContextProvider } from 'crm-cards-sdk/internal/perf/LocationStateContext';
import { useCrmLocationResolverQuery } from './internal/useCrmLocationResolverQuery';
import measure from 'crm-fe-perf/timing/measure';
import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { getCrmLocationResolverStart } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { getRenderApplicationToLocationResolverStart, getLocationResolverToInitialCardsSettled, getLocationResolverToCrmCardLocationStart } from 'crm-fe-perf/timing/measurements/crm-cards-sdk/performanceMeasurements';
import { useIncrementLocationCount, getIsFirstLoad, getWasHidden } from 'crm-cards-sdk/utils/sharedPerfDimensions';
import { useTrackLocationView } from './internal/useTrackLocationView';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useDefaultCrmLocationService = useCrmLocationService;
const INDICATOR_DELAY = 200;
const CrmLocationResolverLoadingState = () => {
  const [pastDelay, setPastDelay] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPastDelay(true);
    }, INDICATOR_DELAY);
    return () => clearTimeout(timeoutId);
  }, []);
  if (pastDelay) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      layout: "centered"
    });
  } else {
    return /*#__PURE__*/_jsx("div", {});
  }
};
const checkCardInLocation = (container, cardId, cardType) => {
  if (container.type === 'CARD') {
    return container.definition.cardId === cardId && container.definition.cardType === cardType;
  }
  if (container.type === 'CONTAINER' && container.contents) {
    return container.contents.some(content => checkCardInLocation(content, cardId, cardType));
  }
  return false;
};
const CrmLocationResolver = ({
  customLayouts,
  additionalLocationData,
  disableUsageTracker,
  editableAreasAtom,
  features,
  gates,
  httpClient = http,
  i18nLoader,
  legacySidebarCardProps,
  location,
  _experimentalTransformCrmLocation,
  _experimentalLocationService,
  crmLocationDataRef,
  dragDropComponents,
  errorNavMarker,
  errorNavMarkerName,
  findInitialFocus,
  loggingTag,
  onQueryFinished,
  propertySource,
  readyNavMarker,
  readyNavMarkerName,
  registry,
  scopes,
  topSlot,
  usageTracker,
  enableVirtualization = false,
  offscreenCardsPolicy,
  disableInternalRhumbMarkers = false
}) => {
  useIncrementLocationCount(location, loggingTag);
  const perfDimensions = useMemo(() => ({
    location,
    logging_tag: loggingTag || '',
    is_first_load: getIsFirstLoad(location, loggingTag)
  }),
  // We don't track it, but we should re-measure whenever the object changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [location, loggingTag, additionalLocationData]);
  useMarkOnMount(getCrmLocationResolverStart(location, loggingTag), perfDimensions);
  useTrackLocationView({
    location,
    loggingTag,
    objectTypeId: additionalLocationData.objectTypeId
  });
  useEffect(() => {
    measure(getRenderApplicationToLocationResolverStart(location, loggingTag), Object.assign({}, perfDimensions, {
      was_hidden: getWasHidden()
    }));
    measure(getLocationResolverToInitialCardsSettled(location, loggingTag), perfDimensions);
    measure(getLocationResolverToCrmCardLocationStart(location, loggingTag), perfDimensions);
  }, [location, loggingTag, perfDimensions]);
  const onQueryFinishedWasCalledRef = useRef(false);
  const cardLocationQueryVariables = useMemo(() => ({
    location,
    objectTypeId: additionalLocationData.objectTypeId,
    objectId: additionalLocationData.objectId
  }), [additionalLocationData.objectId, additionalLocationData.objectTypeId, location]);
  const renderStartedAtMs = useMemo(() => performance.now(),
  /*
   * A new object is effectively a new location render, so we should recalculate the start time.
   * This is necessary in svh-help-desk, where the ticketId may change
   * without a full remount of the location resolver.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cardLocationQueryVariables]);
  const readyMarker = useMemo(() => {
    if (readyNavMarker) {
      return readyNavMarker;
    }
    if (readyNavMarkerName) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: readyNavMarkerName
      });
    }
    return null;
  }, [readyNavMarker, readyNavMarkerName]);
  const errorMarker = useMemo(() => {
    if (errorNavMarker) {
      return errorNavMarker;
    }
    if (errorNavMarkerName) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: errorNavMarkerName
      });
    }
    return null;
  }, [errorNavMarker, errorNavMarkerName]);
  const {
    loading,
    error,
    crmLocation,
    refetch
  } = useCrmLocationResolverQuery({
    location,
    objectId: additionalLocationData.objectId,
    objectTypeId: additionalLocationData.objectTypeId,
    loggingTag,
    transformCrmLocation: _experimentalTransformCrmLocation
  });
  useEffect(() => {
    if (crmLocation && onQueryFinished && !onQueryFinishedWasCalledRef.current) {
      onQueryFinishedWasCalledRef.current = true;
      onQueryFinished();
    }
  }, [crmLocation, onQueryFinished]);
  const service = useCrmLocationService({
    location,
    additionalLocationData,
    httpClient
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_CARD_CONFIGURATION, data => {
    if (crmLocation && checkCardInLocation(crmLocation.container, data.cardId, data.cardType)) {
      refetch().catch(() => {
        // do nothing because this error is handled in Apollo's onError
      });
    }
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT, ({
    objectId,
    objectTypeId
  }) => {
    if (additionalLocationData.objectId === Number(objectId) && additionalLocationData.objectTypeId === objectTypeId) {
      refetch().catch(() => {
        // do nothing because this error is handled in Apollo's onError
      });
    }
  });
  if (crmLocationDataRef) {
    crmLocationDataRef.current = crmLocation;
  }
  if (loading && !crmLocation) {
    return /*#__PURE__*/_jsx(CrmLocationResolverLoadingState, {});
  }
  if (error || !crmLocation) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [errorMarker, /*#__PURE__*/_jsx(CrmLocationErrorMessage, {})]
    });
  }
  const focusedItem = findInitialFocus ? findInitialFocus(crmLocation) : undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [readyMarker, /*#__PURE__*/_jsx(LocationStateContextProvider, {
      location: location,
      loggingTag: loggingTag,
      renderStartedAtMs: renderStartedAtMs,
      isVirtualizationEnabled: enableVirtualization,
      children: /*#__PURE__*/_jsx(CrmCardLocation, {
        customLayouts: customLayouts,
        additionalLocationData: additionalLocationData,
        data: crmLocation,
        disableUsageTracker: disableUsageTracker,
        disableInternalRhumbMarkers: disableInternalRhumbMarkers,
        dragDropComponents: dragDropComponents,
        editableAreasAtom: editableAreasAtom,
        features: features,
        focusedItem: focusedItem,
        gates: gates,
        httpClient: httpClient,
        i18nLoader: i18nLoader,
        legacySidebarCardProps: legacySidebarCardProps,
        location: location,
        locationService: _experimentalLocationService !== undefined ? _experimentalLocationService : service,
        loggingTag: loggingTag,
        propertySource: propertySource,
        registry: registry,
        scopes: scopes,
        topSlot: topSlot,
        usageTracker: usageTracker,
        enableVirtualization: enableVirtualization,
        offscreenCardsPolicy: offscreenCardsPolicy
      })
    })]
  });
};
const CrmLocationResolverWithErrorBoundary = props => {
  return /*#__PURE__*/_jsx(CrmLocationErrorBoundary, {
    source: "CrmLocationResolver",
    location: props.location,
    loggingTag: props.loggingTag,
    children: /*#__PURE__*/_jsx(CrmLocationResolver, Object.assign({}, props))
  });
};
export default /*#__PURE__*/memo(CrmLocationResolverWithErrorBoundary);